import { useEffect, useLayoutEffect } from "react";
import { useCompanySettings } from "./useSettings";
import { hexToHsl } from "@/lib/color-utils";

const BRAND_COLOR_CACHE_KEY = "dallali_brand_color";
/** Written by the removed 4h-TTL cache; pruned on read so it doesn't linger. */
const LEGACY_BRAND_COLOR_TS_KEY = "dallali_brand_color_ts";
/** Branded layouts currently mounted (they can nest) — vars reset at zero. */
let mountedLayouts = 0;

/**
 * Convert any color format to HSL values string for CSS variable.
 * Supports hex (#rrggbb), hsl(h, s%, l%), and raw "h s% l%" formats.
 */
function colorToHslValues(color: string): string | null {
  if (color.startsWith("#")) {
    const hsl = hexToHsl(color);
    if (!hsl) return null;
    return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
  }
  if (color.startsWith("hsl")) {
    const match = color.match(/hsl\((\d+(?:\.\d+)?),?\s*(\d+(?:\.\d+)?)%?,?\s*(\d+(?:\.\d+)?)%?\)/i);
    if (match) {
      return `${parseFloat(match[1])} ${parseFloat(match[2])}% ${parseFloat(match[3])}%`;
    }
  }
  // Already in "h s% l%" format
  if (/^\d+\s+\d+%?\s+\d+%?$/.test(color.trim())) {
    return color.trim();
  }
  return null;
}

/**
 * Apply brand color to all relevant CSS variables.
 * Updates --primary, --sidebar-background (light mode), --ring, and related vars.
 */
export function applyBrandColor(color: string, opts?: { skipCache?: boolean }) {
  const hslValues = colorToHslValues(color);
  if (!hslValues) return;

  const root = document.documentElement;
  const isDark = root.classList.contains("dark");

  // Always update primary color
  root.style.setProperty("--primary", hslValues);
  root.style.setProperty("--ring", hslValues);

  // In light mode, sidebar background is the brand color
  // In dark mode, sidebar background stays dark navy
  if (!isDark) {
    root.style.setProperty("--sidebar-background", hslValues);
    root.style.setProperty("--sidebar-primary-foreground", hslValues);
  }

  // Cache to localStorage for instant application on next load. The public
  // invoice view passes skipCache:true so it never clobbers an authenticated
  // user's cached brand color (which would flash the wrong color on the next
  // dashboard visit in the same tab).
  if (!opts?.skipCache) {
    try {
      localStorage.setItem(BRAND_COLOR_CACHE_KEY, color);
    } catch {
      // Ignore localStorage errors (e.g. private browsing)
    }
  }
}

interface BrandSettings {
  brand_color?: string;
}

/**
 * Reset the brand CSS variables to the stylesheet defaults without touching
 * the cache — the teardown for pages that applied a color with skipCache.
 */
export function resetBrandColorVars() {
  const root = document.documentElement;
  root.style.removeProperty("--primary");
  root.style.removeProperty("--ring");
  root.style.removeProperty("--sidebar-background");
  root.style.removeProperty("--sidebar-primary-foreground");
}

/**
 * Clear brand color cache and reset CSS variables to stylesheet defaults —
 * for when the company has no brand color, so the default must show even
 * while a branded layout is mounted. Use resetBrandColorVars for teardown.
 */
export function clearBrandColorCache() {
  try {
    localStorage.removeItem(BRAND_COLOR_CACHE_KEY);
  } catch {
    // Ignore localStorage errors
  }
  resetBrandColorVars();
}

/**
 * Apply cached brand color synchronously from localStorage.
 * The cache deliberately survives sign-out and never expires: the API
 * response always overwrites it, so a stale value costs at most one brief
 * wrong-brand frame, while a missing one flashes the default purple on
 * every login.
 */
function applyCachedBrandColor() {
  try {
    localStorage.removeItem(LEGACY_BRAND_COLOR_TS_KEY);
    const cached = localStorage.getItem(BRAND_COLOR_CACHE_KEY);
    if (cached) applyBrandColor(cached);
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Hook that loads the saved brand color from company settings
 * and applies it to CSS variables on mount and when settings change.
 * Should be used in AppLayout or a top-level component.
 *
 * Uses localStorage cache to apply the color SYNCHRONOUSLY on first render,
 * preventing the purple flash while waiting for the async API response.
 * The brand vars are reset when the layout unmounts (e.g. sign-out lands on
 * the public login page) — never while the branded layout is still painted.
 */
export function useBrandColor() {
  const { data: company } = useCompanySettings();

  // Apply cached color BEFORE paint (synchronously) to prevent flash
  useLayoutEffect(() => {
    applyCachedBrandColor();
    mountedLayouts += 1;
    return () => {
      mountedLayouts -= 1;
      if (mountedLayouts === 0) resetBrandColorVars();
    };
  }, []);

  // Apply fresh color from API when it arrives (and update cache)
  useEffect(() => {
    if (!company) return; // still loading — keep cached color

    const settings = (company.settings ?? {}) as BrandSettings;
    const savedColor = settings.brand_color;

    if (savedColor) {
      applyBrandColor(savedColor);
    } else {
      // Brand color was removed or never set — clear stale cache
      clearBrandColorCache();
    }
  }, [company]);

  // Also re-apply when theme changes (light/dark toggle)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      // First try cached color for instant response
      applyCachedBrandColor();

      // Then apply from API data if available
      if (!company?.settings) return;
      const settings = company.settings as BrandSettings;
      const savedColor = settings.brand_color;
      if (savedColor) {
        applyBrandColor(savedColor);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [company]);
}
